import { createMuiTheme, ThemeProvider } from "@material-ui/core";
import { BrowserRouter } from 'react-router-dom'
import { Routes } from "./main/routes"
import Box from '@mui/material/Box';
import { Flex } from '@rebass/grid'
import Drawer from '@mui/material/Drawer';
import AuthContextProvider from "./main/context/AuthContext"
import CssBaseline from '@mui/material/CssBaseline';
import AppBar from '@mui/material/AppBar';
import SideMenu from './main/Menu/SideMenu';
import Toolbar from '@mui/material/Toolbar';
import { LookAndFeel, getCompanyLogo } from 'core/utils/lookAndFeel.js'
import { IconButton, makeStyles, createStyles, CircularProgress, Typography, Card, CardContent, Avatar } from '@material-ui/core'
import ExitToAppIcon from '@material-ui/icons/ExitToApp'
import Tooltip from '@material-ui/core/Tooltip'
import React, { useState } from 'react'
import CustomDialog from 'main/components/CustomDialog.js'

const lf = LookAndFeel()

const theme = createMuiTheme({
  palette: {
    background: {
      default: '#FFFFFF'
    }
  },
  typography: {
    fontFamily: [
      'Poppins',
      'sans-serif',
    ].join(','),
    h1: { fontFamily: 'Poppins, sans-serif' },
    h2: { fontFamily: 'Poppins, sans-serif' },
    h3: { fontFamily: 'Poppins, sans-serif' },
    h4: { fontFamily: 'Poppins, sans-serif' },
    h5: { fontFamily: 'Poppins, sans-serif' },
    h6: { fontFamily: 'Poppins, sans-serif' },
    subtitle1: { fontFamily: 'Poppins, sans-serif' },
    subtitle2: { fontFamily: 'Poppins, sans-serif' },
    body1: { fontFamily: 'Poppins, sans-serif' },
    body2: { fontFamily: 'Poppins, sans-serif' },
    button: { fontFamily: 'Poppins, sans-serif' },
    caption: { fontFamily: 'Poppins, sans-serif' },
    overline: { fontFamily: 'Poppins, sans-serif' },
  },
});

const drawerWidth = 240;

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!m) return { r: 0, g: 0, b: 0 }
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  }
}

const mixWithWhite = (hex, weight) => {
  const { r, g, b } = hexToRgb(hex)
  const clamp = v => Math.max(0, Math.min(255, Math.round(v)))
  return `rgb(${clamp(r + (255 - r) * weight)}, ${clamp(g + (255 - g) * weight)}, ${clamp(b + (255 - b) * weight)})`
}

const CLIENT_FLOW_PATHS = [
  '/',
  '/login',
  '/loginusr_name',
  '/loginotp',
  '/simulator',
  '/send-request',
  '/request-in-progress',
  '/preapproval-expired',
  '/preapproval-available',
  '/session-expired',
];

const useStyles = makeStyles(theme =>
  createStyles({
    appMenu: {
      width: '100%',
    },
    navList: {
      width: drawerWidth,
    },
    menuItem: {
      width: drawerWidth,
    },
    menuItemIcon: {
      color: '#97c05c',
    },
    clientAppBar: {
      background: `linear-gradient(180deg, #FFFFFF 0%, ${mixWithWhite(LookAndFeel().buttons.primary, 0.96)} 60%, #FFFFFF 100%)`,
      color: LookAndFeel().text.primary,
      borderBottom: `1px solid ${LookAndFeel().slider.disabled}`,
      boxShadow: `0 1px 2px rgba(17, 24, 39, 0.03), 0 2px 6px ${LookAndFeel().buttons.primary}0B`,
    },
    clientAppBarLogo: {
      height: 40,
      maxWidth: 220,
      objectFit: 'contain',
      [theme.breakpoints.down('sm')]: {
        height: 34,
        maxWidth: 190,
      },
    },
    backofficeAppBar: {
      background: '#2E3B55',
    },
    logoutIcon: {
      color: LookAndFeel().text.primary,
      fontSize: 26,
    },
    logoutIconBackoffice: {
      color: '#FFFFFF',
    },
    drawerLogo: {
      paddingTop: 15,
      paddingLeft: 20,
      paddingRight: 30,
      paddingBottom: 30,
      cursor: 'pointer',
      height: 72,
      maxWidth: 240,
      objectFit: 'contain',
    },
    clientToolbar: {
      minHeight: 80,
      height: 80,
      [theme.breakpoints.down('sm')]: {
        minHeight: 68,
        height: 68,
      },
    },
    loginToolbar: {
      minHeight: 0,
      height: 0,
      padding: 0,
      display: 'none',
    },
    loginAppBar: {
      background: 'transparent',
      boxShadow: 'none',
      borderBottom: 'none',
      height: 0,
      display: 'none',
    },
    contentArea: {
      minHeight: '100vh',
      backgroundColor: '#F5F6F8',
    },
    loginContentArea: {
      minHeight: '100vh',
      backgroundColor: '#FFFFFF',
    },
    backofficeContent: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
    contentFlexColumn: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
    },
    appFooter: {
      marginTop: 'auto',
      width: '100%',
      textAlign: 'center',
      padding: '18px 16px 28px',
      color: '#9CA3AF',
      fontSize: 10.5,
      fontWeight: 500,
      letterSpacing: 0.1,
      fontFamily: "'Poppins', sans-serif",
      [theme.breakpoints.down('sm')]: {
        padding: '14px 12px 26px',
        fontSize: 10,
      },
    },
    logoutOverlay: {
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      backgroundColor: 'rgba(17, 24, 39, 0.55)',
      backdropFilter: 'blur(6px)',
      WebkitBackdropFilter: 'blur(6px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      fontFamily: "'Poppins', sans-serif",
    },
    logoutCard: {
      width: '100%',
      maxWidth: 400,
      borderRadius: 18,
      boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04), 0 10px 32px rgba(17, 24, 39, 0.22)',
      border: `1px solid rgba(17, 24, 39, 0.04)`,
      backgroundColor: '#FFFFFF',
      fontFamily: "'Poppins', sans-serif",
    },
    logoutCardContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: `${theme.spacing(5)}px ${theme.spacing(3)}px`,
      fontFamily: "'Poppins', sans-serif",
      [theme.breakpoints.down('sm')]: {
        padding: `${theme.spacing(4)}px ${theme.spacing(2.5)}px`,
      },
      '&:last-child': {
        paddingBottom: theme.spacing(5),
      },
    },
    logoutAvatarWrap: {
      position: 'relative',
      width: 88,
      height: 88,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing(3),
      fontFamily: "'Poppins', sans-serif",
    },
    logoutCircularBg: {
      position: 'absolute',
      inset: 0,
      color: `${lf.buttons.primary}1F`,
      fontFamily: "'Poppins', sans-serif",
    },
    logoutCircularFg: {
      position: 'absolute',
      inset: 0,
      color: lf.buttons.primary,
      fontFamily: "'Poppins', sans-serif",
    },
    logoutIconAvatar: {
      width: 58,
      height: 58,
      backgroundColor: `${lf.buttons.primary}14`,
      color: lf.text.primary,
      zIndex: 2,
      fontFamily: "'Poppins', sans-serif",
    },
    logoutTitle: {
      color: lf.text.secondary,
      fontWeight: 800,
      fontSize: 22,
      lineHeight: 1.2,
      textAlign: 'center',
      marginBottom: theme.spacing(1),
      fontFamily: "'Poppins', sans-serif",
      [theme.breakpoints.down('sm')]: { fontSize: 19 },
    },
    logoutSubtitle: {
      color: lf.text.base,
      fontSize: 14,
      lineHeight: 1.6,
      textAlign: 'center',
      fontFamily: "'Poppins', sans-serif",
    },
  }),
)

function App() {
  const classes = useStyles()
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false)
  const [logoutLoading, setLogoutLoading] = useState(false)
  const currentPath = (typeof window !== 'undefined' && window.location && window.location.pathname) || ''

  const isClientFlow = (() => {
    if (currentPath === '/' || currentPath === '') return true
    return CLIENT_FLOW_PATHS.some(path =>
      currentPath === path || currentPath.startsWith('/private-payroll')
    )
  })()

  const isLogin = (() => {
    return currentPath === '/' ||
      currentPath === '/login' ||
      currentPath === '/loginusr_name' ||
      currentPath === '/loginotp' ||
      currentPath === '/session-expired'
  })()

  const showDrawer = !isClientFlow

  const appBarClass = (() => {
    if (showDrawer) return classes.backofficeAppBar
    if (isLogin) return classes.loginAppBar
    return classes.clientAppBar
  })()

  const toolbarClass = isLogin ? classes.loginToolbar : classes.clientToolbar
  const contentAreaClass = isLogin ? classes.loginContentArea : classes.contentArea
  const appBarWidth = showDrawer ? `calc(100% - ${drawerWidth}px)` : '100%'
  const appBarMl = showDrawer ? `${drawerWidth}px` : 0

  const handleLogOut = () => {
    setConfirmLogoutOpen(true)
  }

  const handleConfirmLogoutCancel = () => {
    setConfirmLogoutOpen(false)
  }

  const handleConfirmLogoutOk = async () => {
    setConfirmLogoutOpen(false)
    setLogoutLoading(true)
    const basename = window.__POWERED_BY_QIANKUN__ ? '/private-payroll' : ''
    try {
      await new Promise(r => setTimeout(r, 900))
      try {
        window.sessionStorage.removeItem('user')
        window.sessionStorage.removeItem('token')
        window.sessionStorage.removeItem('uuid')
        window.sessionStorage.removeItem('payrollAdvance:sendRequest')
      } catch (_e) { /* no-op */ }
      document.location.replace(`${basename}/login`)
    } catch (_e) {
      document.location.replace(`${basename}/login`)
    }
  }

  const handleClickLogo = () => {
    document.location.replace("/menu-backoffice")
  }

  return (
    <BrowserRouter basename={window.__POWERED_BY_QIANKUN__ ? '/private-payroll' : '/'}>
      <ThemeProvider theme={theme}>
        <AuthContextProvider>
          <Box className={contentAreaClass}>
            <CssBaseline />
            <AppBar
              position="fixed"
              elevation={0}
              className={appBarClass}
              sx={{ width: appBarWidth, ml: appBarMl }}
            >
              <Toolbar className={toolbarClass}>
                {!isLogin && (
                  <Flex
                    width={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      {!showDrawer && (
                        <img
                          className={classes.clientAppBarLogo}
                          alt="logo"
                          src={getCompanyLogo()}
                        />
                      )}
                    </Box>
                    <Box>
                      <Tooltip title="Cerrar sesión" arrow>
                        <IconButton
                          edge="end"
                          aria-label="logout"
                          onClick={handleLogOut}
                        >
                          <ExitToAppIcon className={showDrawer ? classes.logoutIconBackoffice : classes.logoutIcon} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Flex>
                )}
              </Toolbar>
            </AppBar>

            {showDrawer && (
              <Drawer
                sx={{
                  width: drawerWidth,
                  flexShrink: 0,
                  '& .MuiDrawer-paper': {
                    width: drawerWidth,
                    boxSizing: 'border-box',
                  },
                }}
                variant="permanent"
                anchor="left"
              >
                <img
                  className={classes.drawerLogo}
                  alt="logo"
                  onClick={handleClickLogo}
                  src={getCompanyLogo()}
                />
                <SideMenu />
              </Drawer>
            )}

            <Box className={`${showDrawer ? classes.backofficeContent : ''}`}>
              {!isLogin && <Toolbar className={classes.clientToolbar} />}
              <Routes />
            </Box>

            <CustomDialog
              isOpen={confirmLogoutOpen}
              title="Cerrar sesión"
              message="¿Estás seguro de que quieres cerrar sesión?"
              okLabel="Sí, cerrar sesión"
              cancelLabel="Cancelar"
              onOkButton={handleConfirmLogoutOk}
              onCancelButton={handleConfirmLogoutCancel}
              onClose={handleConfirmLogoutCancel}
            />

            {logoutLoading && (
              <div className={classes.logoutOverlay}>
                <Card className={classes.logoutCard}>
                  <CardContent className={classes.logoutCardContent}>
                    <Box className={classes.logoutAvatarWrap}>
                      <CircularProgress
                        variant="determinate"
                        value={100}
                        size={88}
                        thickness={1.8}
                        className={classes.logoutCircularBg}
                      />
                      <CircularProgress
                        variant="indeterminate"
                        size={88}
                        thickness={1.8}
                        disableShrink
                        className={classes.logoutCircularFg}
                      />
                      <Avatar className={classes.logoutIconAvatar}>
                        <ExitToAppIcon style={{ fontSize: 28 }} />
                      </Avatar>
                    </Box>
                    <Typography className={classes.logoutTitle}>
                      Cerrando&nbsp;sesión
                    </Typography>
                    <Typography className={classes.logoutSubtitle}>
                      Estamos cerrando tu sesión de forma segura. Espera un momento.
                    </Typography>
                  </CardContent>
                </Card>
              </div>
            )}
          </Box>
        </AuthContextProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
